import React from 'react';

/**
 * Phase 5.G PR-2 — search + status filter strip rendered above the
 * mobile gates list.
 *
 * Status chips ("הכל / פתוחים / סגורים / לא מוגדר") drive both the
 * counter under each chip and the actual list rows. Counts reflect
 * the full site (before the search query is applied) so the user can
 * tell whether their text query is the thing hiding results.
 *
 * Stateless: the parent owns ``query`` and ``status``. Caller filters
 * the gates list with the helper below to keep mobile cards and the
 * desktop table consistent (desktop currently ignores these controls,
 * but the helper is exported so adopting it on desktop is one prop
 * away).
 */

export const STATUS_OPTIONS = [
  { value: 'all', label: 'הכל' },
  { value: 'open', label: 'פתוחים' },
  { value: 'closed', label: 'סגורים' },
  { value: 'unset', label: 'לא מוגדר' },
];

export function gateMatchesStatus(gate, status) {
  if (status === 'all') return true;
  if (status === 'unset') return gate.installation_status === 'not_started';
  if (gate.installation_status === 'not_started') return false;
  if (status === 'open') return !!gate.is_open;
  if (status === 'closed') return !gate.is_open;
  return true;
}

export function gateMatchesQuery(gate, queryLower) {
  if (!queryLower) return true;
  const haystack = [
    gate.name,
    gate.model,
    gate.group_name,
    gate.sheli_id,
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(queryLower);
}

export function filterGates(gates, { query, status }) {
  const q = (query || '').trim().toLowerCase();
  return gates.filter(g => gateMatchesStatus(g, status) && gateMatchesQuery(g, q));
}

function statusCount(gates, status) {
  if (status === 'all') return gates.length;
  return gates.filter(g => gateMatchesStatus(g, status)).length;
}

function GatesListFilters({ gates, query, onQueryChange, status, onStatusChange }) {
  return (
    <div className="biz-list-filters">
      <input
        type="search"
        className="biz-list-search"
        placeholder="חיפוש שער..."
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        aria-label="חיפוש שער"
      />
      <div className="biz-list-status-chips" role="tablist" aria-label="סינון לפי סטטוס">
        {STATUS_OPTIONS.map(opt => {
          const active = opt.value === status;
          const count = statusCount(gates, opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={active}
              className={`biz-list-status-chip${active ? ' is-active' : ''}`}
              onClick={() => onStatusChange(opt.value)}
            >
              {opt.label}
              <span className="biz-list-status-chip-count">{count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default GatesListFilters;
